import _ from 'lodash';
import app from '$$app';
import constants from '$$helpers/constants';
import rights from '../../../components/rights';
import customHelper from '../../../helpers/custom';
import globalToast from '$$vueCmp/toast';

class Tasks {
  #instance;

  oldParentIdOnTaskMove = null;

  #isRemovingTaskId = null;

  init(instance) {
    this.#instance = instance;
  }

  addSubTask(parentTaskId) {
    const parentTaskData = this.#instance.getTask(parentTaskId);
    let taskId = 0;
    const resourceId = this.#instance.ganttDataApi.getResourceByUserId(user.id)?.id;

    if (this.#instance.calculateTaskLevel(parentTaskId) > 12) {
      // MYSQL: Cascading operations may not be nested more than 15 levels deep.
      return false;
    }

    // gantt.callEvent('onChangeTaskToParent', [parentTaskData]);

    if (!this.#instance.hasChild(parentTaskId)) {
      taskId = this.#instance.createTask(
        {
          text: __('gantt_new_subtask'),
          type: this.#instance.config.types.task,
          duration: parentTaskData.duration,
          estimation: 0,
          ignoreProgress: true,
          gantt_id: parentTaskData.gantt_id,
          calendar_id: parentTaskData.gantt_id,
          status: 1,
          created_at: new Date(),
          sortorder: this.#instance.getChildren(parentTaskId).length,
          owner_id: resourceId,
          openEditAfterCreate: true,
        },
        +parentTaskId,
      );

      parentTaskData.type = this.#instance.config.types.project;
      parentTaskData.open = 1;
      parentTaskData.progress = 0;
      parentTaskData.color = constants.DEFAULT_TYPE_COLORS.GROUP;

      // innerObject.handlersFromDependencyModule.deleteResourcesFromTask(parentTaskData);

      this.#instance.updateTask(parentTaskId, parentTaskData);
      this.addButtonTo(parentTaskData);

      return true;
    }

    const calcStartDate = new Date();
    const projectStartDate = parentTaskData.start_date;
    let startDate;
    const projectCalendar = this.#instance.getCalendar(parentTaskData.gantt_id);

    if (projectStartDate.valueOf() > calcStartDate.valueOf()) {
      startDate = projectStartDate;
    } else {
      startDate = calcStartDate;
    }

    if (!this.#instance.isWorkDay(startDate, projectCalendar)) {
      startDate = this.#instance.getClosestWorkTime({
        date: startDate, dir: 'future', unit: this.#instance.config.duration_unit, task: parentTaskData,
      });
    }

    const workRange = this.#instance.getDayHours(startDate);
    const dayStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), workRange.firstHour);

    startDate = this.#instance.getClosestWorkTime({ date: dayStart, dir: 'future' });

    if (projectStartDate.valueOf() > startDate.valueOf()) {
      startDate = this.#instance.getClosestWorkTime({ date: this.#instance.date.add(startDate, 1, 'day'), dir: 'future' });
    }

    const oldDurationView = this.#instance.config.duration_view;
    const projectConfig = this.#instance.ganttDataApi.getActiveProjectConfig();

    if (this.#instance.config.duration_view === 'hour') {
      this.#instance.config.duration_view = projectConfig.durationData.mode;
    }

    const endDateData = this.#instance.calculateToDuration(startDate, 1);
    const duration = this.#instance.calculateDuration(startDate, endDateData.endDate, parentTaskData);

    this.#instance.config.duration_view = oldDurationView;
    const sortOrderAndIndex = this.calculateSortOrderForTask(parentTaskData.id);

    const newTask = {
      text: __('gantt_new_subtask'),
      start_date: startDate,
      duration,
      estimation: 0,
      type: this.#instance.config.types.task,
      created_at: new Date(),
      sortorder: sortOrderAndIndex.sortorder,
      gantt_id: parentTaskData.gantt_id,
      owner_id: resourceId,
      calendar_id: parentTaskData.gantt_id,
      openEditAfterCreate: true,
      resources: [],
    };

    taskId = this.#instance.createTask(newTask, +parentTaskId, sortOrderAndIndex.index);

    parentTaskData.open = 1;

    this.#instance.updateTask(parentTaskId, parentTaskData);
  }

  addSiblingTask(sourceTaskId) {
    const task = this.#instance.getTask(sourceTaskId);
    const calcStartDate = this.#instance.date.hour_start(new Date());
    const projectStartDate = this.#instance.getTask(task.parent).start_date;
    const projectConfig = this.#instance.ganttDataApi.getActiveProjectConfig();
    const oldDurationView = this.#instance.config.duration_view;
    let duration;
    let startDate;
    const projectCalendar = this.#instance.getCalendar(task.gantt_id);

    if (projectStartDate.valueOf() > calcStartDate.valueOf()) {
      startDate = projectStartDate;
    } else {
      startDate = calcStartDate;
    }

    if (!this.#instance.isWorkDay(startDate, projectCalendar)) {
      startDate = this.#instance.getClosestWorkTime({
        date: startDate, task, dir: 'future', unit: this.#instance.config.duration_unit,
      });
    }

    const workRange = this.#instance.getDayHours(startDate);
    const dayStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), workRange.firstHour);

    startDate = this.#instance.getClosestWorkTime({ date: dayStart, task, dir: 'future' });

    if (projectStartDate.valueOf() > startDate.valueOf()) {
      startDate = this.#instance.getClosestWorkTime({ date: this.#instance.date.add(startDate, 1, 'day'), task, dir: 'future' });
    }

    if (this.#instance.config.duration_view === 'hour') {
      this.#instance.config.duration_view = projectConfig.durationData.mode;
    }

    const endDateData = this.#instance.calculateToDuration(startDate, 1);

    duration = this.#instance.calculateDuration(startDate, endDateData.endDate, task);
    this.#instance.config.duration_view = oldDurationView;

    const children = this.#instance.getChildren(task.parent);
    let childrenIndex = 0;

    _.some(children, child => {
      ++childrenIndex;
      if (+child === task.id) {
        return true;
      }
    });

    let checkAfterCurrentTask = false;

    _.each(children, child => {
      if (checkAfterCurrentTask) {
        const childTask = this.#instance.getTask(child);

        childTask.sortorder = +childTask.sortorder + 1;
      } else if (+child === task.id) {
        checkAfterCurrentTask = true;
      }
    });

    const resourceId = this.#instance.ganttDataApi.getResourceByUserId(user.id)?.id;

    this.#instance.createTask(
      {
        text: __('gantt_new_sibling_task'),
        duration,
        estimation: 0,
        insertAfter: JSON.stringify([task.id, task.parent]),
        type: this.#instance.config.types.task,
        start_date: startDate,
        sortorder: +task.sortorder + 1,
        gantt_id: task.gantt_id,
        calendar_id: task.gantt_id,
        created_at: new Date(),
        owner_id: resourceId,
        openEditAfterCreate: true,
        resources: [],
      },
      task.parent,
      childrenIndex,
    );
  }

  addSubMilestone(parentTaskId) {
    const taskData = this.#instance.getTask(parentTaskId);
    let taskId = 0;
    const resourceId = this.#instance.ganttDataApi.getResourceByUserId(user.id)?.id;

    // this.#instance.callEvent('onChangeTaskToParent', [taskData]);

    if (!this.#instance.hasChild(parentTaskId)) {
      taskId = this.#instance.createTask(
        {
          text: __('gantt_new_child_milestone'),
          type: this.#instance.config.types.milestone,
          gantt_id: taskData.gantt_id,
          calendar_id: taskData.gantt_id,
          color: 16,
          ignoreProgress: true,
          created_at: new Date(),
          sortorder: 1,
          owner_id: resourceId,
          openEditAfterCreate: true,
          resources: [],
        },
        +parentTaskId,
      );

      return;

      // this.#instance.callEvent('customButtons:add:to', [taskData]);
      // taskData.type = 'project';
      // taskData.open = 1;
      // taskData.progress = 0;
      // taskData.actionHash = this.#instance.getTask(taskId).actionHash;
      // taskData.actionHashManual = true;
      //
      // innerObject.handlersFromDependencyModule.deleteResourcesFromTask(taskData);
      //
      // this.#instance.callEvent('setIgnoreMove', [taskData.id]);
      //
      // this.#instance.callEvent('customButtons:add:to', [taskData]);
      // this.#instance.updateTask(currentTaskId, taskData);
      //
      // delete taskData.actionHash;
      // delete taskData.actionHashManual;
      //
      // return true;
    }

    const children = this.#instance.getChildren(parentTaskId);
    let sortorder = 1;

    _.each(children, child => {
      const childTask = this.#instance.getTask(child);

      if (childTask.type !== this.#instance.config.types.button && +childTask.sortorder >= sortorder) {
        sortorder = +childTask.sortorder;
      }
    });

    taskId = this.#instance.createTask({
      text: __('gantt_new_child_milestone'),
      type: this.#instance.config.types.milestone,
      created_at: new Date(),
      gantt_id: taskData.gantt_id,
      calendar_id: taskData.gantt_id,
      sortorder: sortorder + 1,
      owner_id: resourceId,
      openEditAfterCreate: true,
      resources: [],
    },
    +parentTaskId,
    this.#instance.getChildCount(parentTaskId) - 1);

    taskData.open = 1;

    this.#instance.updateTask(parentTaskId, taskData);
  }

  addSiblingMilestone(currentSiblingTaskId) {
    const task = this.#instance.getTask(currentSiblingTaskId);
    const children = this.#instance.getChildren(task.parent);
    let childrenIndex = 0;
    const resourceId = this.#instance.ganttDataApi.getResourceByUserId(user.id)?.id;

    _.some(children, child => {
      ++childrenIndex;
      if (+child === task.id) {
        return true;
      }
    });

    let checkAfterCurrentTask = false;

    _.each(children, child => {
      if (checkAfterCurrentTask) {
        const childTask = this.#instance.getTask(child);

        childTask.sortorder = +childTask.sortorder + 1;
      } else if (+child === task.id) {
        checkAfterCurrentTask = true;
      }
    });

    this.#instance.createTask(
      {
        text: __('gantt_new_sibling_milestone'),
        type: this.#instance.config.types.milestone,
        gantt_id: task.gantt_id,
        calendar_id: task.gantt_id,
        sortorder: +task.sortorder + 1,
        insertAfter: JSON.stringify([task.id, task.parent]),
        created_at: new Date(),
        owner_id: resourceId,
        openEditAfterCreate: true,
        resources: [],
      },
      task.parent,
      childrenIndex,
    );
  }

  moveIndent(taskId) {
    let newParentId = this.#instance.getPrevSibling(taskId);
    // const oldParentId = this.#instance.getParent(taskId);
    let newParent = {};
    let maxCurrentLevelDepth = 0;
    let maxSiblingLevelDepth = 0;

    if (!newParentId) {
      return false;
    }

    maxSiblingLevelDepth = this.#instance.calculateTaskLevel(newParentId);
    maxCurrentLevelDepth = this.#instance.calculateTaskLevel(taskId);

    if (this.#instance.hasChild(taskId)) {
      this.#instance.eachTask(task => {
        if (this.#instance.calculateTaskLevel(task.id) > maxCurrentLevelDepth) {
          maxCurrentLevelDepth = this.#instance.calculateTaskLevel(task.id);
        }
      }, taskId);
    }

    if ((maxCurrentLevelDepth + maxSiblingLevelDepth) > 15) {
      globalToast.info(__('block_indent_parent_task_message'), { timeout: 4000 });

      return false;
    }

    newParent = this.#instance.getTask(newParentId);

    while (newParent.type === this.#instance.config.types.button || !this.#instance.calculateTaskLevel(newParentId)) {
      newParentId = this.#instance.getPrevSibling(newParentId);
      newParent = this.#instance.getTask(newParentId);
    }

    this.#instance.moveTask(+taskId, this.#instance.getChildren(newParentId).length - 1, newParentId);
    this.#instance.updateTask(taskId);
  }

  moveOutdent(taskId) {
    const currentTask = this.#instance.getTask(taskId);
    const oldParent = this.#instance.getTask(currentTask.parent);
    const newParentId = this.#instance.getParent(oldParent.id);
    const newParent = this.#instance.isTaskExists(newParentId) && this.#instance.getTask(newParentId);

    const viewData = this.#instance.ganttDataApi.getActiveProjectData();

    if (!newParent || (viewData.is_jira && newParent.$level === 0)) {
      return false;
    }

    if (this.#instance.isTaskExists(oldParent.id) && this.#instance.calculateTaskLevel(oldParent) !== this.#instance.config.root_id) {
      this.#instance.moveTask(+taskId, this.#instance.getTaskIndex(oldParent.id), newParent.id);
      this.#instance.updateTask(taskId);
    }
  }

  convertType(taskId) {
    const taskData = this.#instance.getTask(taskId);

    if (taskData.type === this.#instance.config.types.milestone) {
      taskData.type = this.#instance.config.types.task;
      taskData.color = constants.DEFAULT_TYPE_COLORS.TASK;
      taskData.duration = 60;
    } else {
      taskData.type = this.#instance.config.types.milestone;
      taskData.color = constants.DEFAULT_TYPE_COLORS.MILESTONE;
      taskData.duration = 0;
    }

    taskData.estimation = 0;
    taskData.end_date = this.#instance.calculateEndDate(taskData);
    this.#instance.updateTask(taskId, taskData);
  }

  delete(taskId) {
    if (!this.#instance.isTaskExists(taskId)) return;

    const taskData = this.#instance.getTask(taskId);

    if (taskData.type !== this.#instance.config.types.button) {
      app.trigger('popup:show', true);
      this.#isRemovingTaskId = +taskId;

      const confirmCallback = data => {
        this.#isRemovingTaskId = null;
        this.#instance.deleteTask(taskId);

        this.#instance.callEvent('onCloseGlobalPopup');
        userExtAnalytics.log('gantt_task_delete_confirmed');
      };
      const cancelCallback = () => {
        this.#isRemovingTaskId = null;
        userExtAnalytics.log('gantt_task_delete_cancelled');
      };
      const isJira = !!taskData.jira_key;
      const localeKey = 'settings_msg_delete_task' + (isJira ? '_jira' : '');
      const taskName = __(localeKey, { taskName: `<b>${customHelper.formatTaskName(taskData.text)}</b>` });

      this.#instance.callEvent('onShowGlobalPopup', [{
        type: 'confirm',
        ok: __('common_yes'),
        cancel: __('common_cancel'),
        message: `<div>${taskName}</div>`,
        footerText: true,
        popupWidth: 440,
        buttonWidth: '90px',
        confirm: confirmCallback,
        cancelCallback: cancelCallback,
      }]);
    }
  }

  checkRemovingTask() {
    if (this.#isRemovingTaskId && !this.#instance.isTaskExists(this.#isRemovingTaskId)) {
      this.#isRemovingTaskId = null;
      this.#instance.callEvent('onCloseGlobalPopup');
      userExtAnalytics.log('gantt_task_delete_cancelled');
    }
  }

  onAfterTaskDelete(taskId, task) {
    if (task.type === this.#instance.config.types.button || !this.#instance.isTaskExists(task.parent)) {
      return;
    }

    const parentTaskData = webix.copy(this.#instance.getTask(task.parent));

    const children = _.filter(this.#instance.getChildren(parentTaskData.id), childID => {
      const childData = this.#instance.getTask(childID) || {};

      return childData.type !== this.#instance.config.types.button;
    });

    const isProjectLevel = this.#instance.config.multiview ? 1 : 0;

    if (
      children.length < 1
      && parentTaskData.type === this.#instance.config.types.project
      && this.#instance.calculateTaskLevel(parentTaskData.id) > isProjectLevel
    ) {
      parentTaskData.type = this.#instance.config.types.task;
      parentTaskData.progress = 0;
      // parentTaskData.color = constants.DEFAULT_TYPE_COLORS.TASK;

      if (parentTaskData.end_date.valueOf() <= parentTaskData.start_date.valueOf()) {
        parentTaskData.end_date = this.#instance.calculateEndDate({
          start_date: parentTaskData.start_date,
          duration: 1,
          task: parentTaskData,
        });
      }

      this.removeButtonFrom(parentTaskData);
      this.#instance.updateTask(parentTaskData.id, parentTaskData);
    }
  }

  addButtonsToProjectTasks() {
    if (this.#instance.config.isWorkload || this.#instance.ganttDataApi.isExport() || this.#instance.ganttDataApi.isLink() || this.#instance.ganttDataApi.isHistory()) {
      return;
    }

    const tasks = this.#instance.getTaskBy(task => task.type === 'project');
    const newTasks = [];

    tasks.forEach(task => {
      // multiview main parent
      if (this.#instance.config.multiview && task.id === 1) return;

      const children = this.#instance.getChildren(task.id);

      if ((children.length || !task.parent || task.parent === 1) && rights.project.hasRight(task.gantt_id, 'create_task')) {
        newTasks.push(
          {
            id: `${task.id}_buttons`,
            unscheduled: true,
            auto_scheduling: false,
            type: this.#instance.config.types.button,
            gantt_id: task.gantt_id,
            parent: task.id,
            order: children.length + 1,
            hide_bar: true,
          },
        );
      }
    });

    this.#instance.silent(() => {
      this.#instance._ganttPreventFilter = true;
      this.#instance.parse({ data: newTasks, links: [] }, 'json');
    });
  }

  addButtonTo(task, indexButton) {
    if (rights.project.hasRight(task.gantt_id, 'create_task')) {
      const id = `${task.id}_buttons`;

      this.#instance.silent(() => {
        this.#instance.addTask({
          id,
          unscheduled: true,
          auto_scheduling: false,
          type: this.#instance.config.types.button,
          gantt_id: task.gantt_id,
          parent: task.id,
          hide_bar: true,
        }, task.id, indexButton);

        if (!this.#instance.$data.tasksStore.fullOrder.includes(id)) {
          this.#instance.$data.tasksStore.fullOrder.push(id);
        }
      });
    }
  }

  removeButtonFrom(task) {
    const buttonTaskId = this.#instance.getChildren(task.id)
      .find(childId => this.#instance.getTask(childId).type === this.#instance.config.types.button);

    if (this.#instance.isTaskExists(buttonTaskId)) {
      this.#instance.silent(() => {
        this.#instance.deleteTask(buttonTaskId);
      });
    }
  }

  toggleTask(taskId) {
    const task = this.#instance.getTask(taskId);

    if (!task.$open) {
      task.$open = 1;
      task.open = 1;
    } else {
      task.$open = 0;
      task.open = 0;
    }

    this.#instance.updateTask(task.id);
    this.#instance.$layout.resize();
  }

  createTaskFromGridButton(task) {
    const parent = this.#instance.getTask(task.parent);

    Object.assign(task, this.#instance.ganttDataApi.getDefaultCustomValues(parent.gantt_id));

    task.gantt_id = parent.gantt_id;
    task.start_date = this.calculateStarDateForTask(task);
    task.estimation = 0;
    task.owner_id = this.#instance.ganttDataApi.getResourceByUserId(user.id)?.id;
    task.deadline = null;

    if (task.type === this.#instance.config.types.task) {
      const endDate = this.#instance.calculateEndDate({
        start_date: task.start_date,
        duration: 1,
        unit: this.#instance.config.durationData.mode,
        task: parent,
      });

      const calculatedDuration = this.#instance.calculateDuration(task.start_date, endDate, parent);

      task.end_date = this.#instance.calculateEndDate({
        start_date: task.start_date,
        duration: calculatedDuration,
        unit: this.#instance.config.duration_unit,
        task: parent,
      });

      task.duration = calculatedDuration;
      task.calendar_id = parent.calendar_id;
    }

    const { index, sortorder } = this.calculateSortOrderForTask(parent.id);

    task.sortorder = sortorder;

    return this.#instance.createTask({ ...task }, parent.id, index);
  }

  calculateSortOrderForTask(parentId) {
    const children = this.#instance.getChildren(parentId)
      .map(it => this.#instance.getTask(it))
      .filter(fit => fit.type !== this.#instance.config.types.button);

    const childrenLength = (children && children.length) || 0;
    const sortOrder = (children && children.length) ? (+children[children.length - 1].sortorder + 1) : 1;

    return {
      index: childrenLength,
      sortorder: sortOrder,
    };
  }

  calculateStarDateForTask(task) {
    const parentTask = this.#instance.getTask(task.parent);
    const projectCalendar = this.#instance.getCalendar(parentTask.gantt_id);

    const now = new Date();
    let startDate = parentTask.start_date.valueOf() > now.valueOf() ? parentTask.start_date : now;

    if (!this.#instance.isWorkDay(startDate, projectCalendar)) {
      startDate = this.#instance.getClosestWorkTime({
        date: startDate, task: parentTask, dir: 'future', unit: this.#instance.config.duration_unit,
      });
    }

    if (task.type === this.#instance.config.types.task || task.type === this.#instance.config.types.milestone) {
      const workRange = this.#instance.getDayHours(startDate);
      const dayStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), workRange.firstHour);

      startDate = this.#instance.getClosestWorkTime({ date: dayStart, task: parentTask, dir: 'future' });

      if (parentTask.start_date.valueOf() > startDate.valueOf()) {
        startDate = this.#instance.getClosestWorkTime({
          date: this.#instance.date.add(startDate, 1, 'day'),
          task: parentTask,
          dir: 'future',
        });
      }
    }

    return startDate;
  }

  onBeforeTaskMove(taskId) {
    if (!this.#instance.isTaskExists(taskId)) return false;

    const task = this.#instance.getTask(taskId);

    if (task.type === this.#instance.config.types.button) return false;

    this.oldParentIdOnTaskMove = task.parent;

    return true;
  }

  onAfterTaskMove(taskId, newParentId) {
    const sourceTask = this.#instance.getTask(taskId);
    const oldParent = this.#instance.getTask(this.oldParentIdOnTaskMove);
    const newParent = this.#instance.getTask(sourceTask.parent);

    const oldParentChildren = _.filter(this.#instance.getChildren(oldParent.id), tId => {
      const tData = this.#instance.getTask(tId);

      return tData.type !== this.#instance.config.types.button;
    });

    if (oldParentChildren.length === 0) {
      this.removeButtonFrom(oldParent);
    }

    if (this.#instance.getChildren(newParent.id).length === 1) {
      this.addButtonTo(newParent);
    } else {
      this.removeButtonFrom(newParent);
      this.addButtonTo(newParent);
    }

    this.oldParentIdOnTaskMove = null;

    this.#instance.batchUpdate(() => {
      this.#instance.eachTask(task => {
        if (task.type !== this.#instance.config.types.button) {
          const taskIndex = this.#instance.getTaskIndex(task.id);
          const hasChildren = this.#instance.hasChild(task.id);
          let isTaskChanged = false;

          if (taskIndex !== task.sortorder && task.gantt_id === sourceTask.gantt_id) {
            isTaskChanged = true;
            task.sortorder = taskIndex;
          }

          if (hasChildren && (task.type === this.#instance.config.types.task || task.type === this.#instance.config.types.milestone)) {
            isTaskChanged = true;
            task.type = this.#instance.config.types.project;
            task.open = 1;
          }

          if (!hasChildren && task.type === this.#instance.config.types.project) {
            isTaskChanged = true;
            task.type = this.#instance.config.types.task;
            task.duration = 1;
            task.end_date = this.#instance.calculateEndDate(task);
          }

          if (task.status > 1 && task.type === this.#instance.config.types.project && !this.#instance.config.isJira) {
            isTaskChanged = true;
            task.status = 1;
          }

          if (isTaskChanged) {
            this.#instance.updateTask(task.id);
          }
        }
      });
    });
  }

  selectAndShowTask(id) {
    const task = this.#instance.getTask(id);

    this.#instance.selectTask(task.id);

    const yScroll = this.#instance.getScrollState().y;

    this.#instance.showTask(task.id);

    const xScroll = this.#instance.getScrollState().x;

    // this.#instance._blockScrollEvent = true;
    this.#instance.scrollTo(xScroll, yScroll);
  }

  setHoverTaskRow(taskId) {
    const instance = this.#instance;
    const container = instance.$container;

    if (!container) {
      return;
    }

    if (!taskId) {
      instance._currentHoverRowId = null;
    }

    if (instance.getState().link_source_id) {
      return;
    }

    const oldTasks = container.querySelectorAll(`.row_hover:not([task_id='${taskId}'])`);

    oldTasks.forEach(task => this.removeHoverForTaskRow(task));

    if (Number(instance._currentHoverRowId) === Number(taskId)) {
      return;
    }

    const taskRow = container.querySelector(`.gantt_task_row[${instance.config.task_attribute}='${taskId}']`);
    const gridRow = container.querySelector(`.gantt_row[${instance.config.task_attribute}='${taskId}']`);

    if (!taskRow || !gridRow) {
      return;
    }

    instance._currentHoverRowId = taskId;

    [gridRow, taskRow].forEach(row => row.classList.add('row_hover'));

  }

  removeHoverForTaskRow(element) {
    this.#instance._currentHoverRowId = null;
    element?.classList?.remove('row_hover');
  }

  selectTask(taskId) {
    this.#instance.eachParent(task => {
      if (!task.open) {
        this.toggleTask(task.id);
      }
    }, +taskId);

    if (this.#instance.isTaskExists(+taskId)) {
      this.#instance.showTask(+taskId);// for horizontal scrolling
      this.#instance.config._opened_task_id = +taskId;// for inline editors
      this.#instance.$grid.classList.add('opened_task_settings');
    }
  }

  unselectTask() {
    this.#instance.$grid?.classList.remove('opened_task_settings');
    this.#instance.config._opened_task_id = null;
    this.#instance.unselectTask();
  }
}

export default new Tasks();
